/* ============================================================
   o kama sona — sentence composer engine ("nasin toki")
   ------------------------------------------------------------
   A small parser + glosser for learner-built toki pona sentences.
   Feed it a tile row ("tomo pi telo nasa li pona") and it returns
   every reasonable reading, each with a deliberately BROAD English
   gloss — "structure of [strange water]", never "bar" — so learners
   see how meaning is composed instead of memorizing lexicalized
   translations.

   Design decisions (agreed for the compose screen):
   - Ambiguity is a feature: preverb-vs-head and prep-vs-modifier
     readings are all returned, ranked by plausibility, never hidden.
   - pi groups the words after it (two or more) into one modifier.
     Sibling pi groups are allowed; pi inside pi is not expressible
     (each pi starts a new sibling group) — matching community norms.
   - Structural mistakes return friendly errors that echo the grammar
     PATTERNS the learner has met (missing li, pi + one word, etc).
   - Readings can be gated by unlocked grammar patterns (see
     `unlockedPatternIds`), so the sandbox never shows a preverb or
     preposition reading before that lesson is reached.

   Shapes:
   - NP:      { head, items: [ {kind:'mod', word} | {kind:'neg'}
                             | {kind:'pi', np: NP} ] }
   - Pred:    { kind:'verbal'|'copular'|'prepHead'|'become',
                head, np, preverbs:[{word,neg}], objects:[NP],
                preps:[{word, np}], ynq }
   - Reading: { gloss, score, tags:[...], tree }
   - Result:  { input, tokens, readings:[Reading], errors:[{code,
                message, hint?}], question, emphasis }
   ============================================================ */

import { WORDS, PARTICLES, PATTERNS } from "./tokiPonaData";

/* ---------------- role glosses ----------------
   For every word: how it glosses in each grammatical role. Key order
   is the word's PREFERRED role (first key = most natural reading, used
   for ranking). Senses are " / "-separated; composition uses the first
   sense, the full string is available for popovers. Kept intentionally
   broad and compositional — "indoor space", not "house"; "blue-green",
   not "blue" — so built compounds never collapse into one English word. */
export const ROLE_GLOSSES = {
  // U1 · core
  mi: { noun: "I / we", mod: "my / our" },
  sina: { noun: "you", mod: "your" },
  ona: { noun: "they / it", mod: "their / its" },
  jan: { noun: "person / being", mod: "human / personal" },
  ni: { noun: "this / that", mod: "this / that" },
  ijo: { noun: "thing / object", mod: "thing-like" },
  toki: { verb: "talk / say", noun: "talk / language", mod: "talking / verbal", vt: "say" },
  nimi: { noun: "word / name", verb: "name / call", mod: "named" },
  // U2 · qualities
  pona: { mod: "good / simple", noun: "goodness", verb: "improve / fix", vadv: "well" },
  ike: { mod: "bad / complicated", noun: "badness", verb: "worsen / hurt", vadv: "badly" },
  suli: { mod: "big / important", noun: "bigness / importance" },
  lili: { mod: "small / few", adv: "slightly", noun: "smallness", vadv: "a little" },
  mute: { mod: "many / much", adv: "very", noun: "a lot", vadv: "a lot" },
  ala: { mod: "no / not", adv: "not", noun: "nothing" },
  sin: { mod: "new / fresh", noun: "new thing", verb: "renew / add", vadv: "again" },
  sama: { mod: "same / similar", noun: "sameness / peer" },
  // U3 · colors
  loje: { mod: "red", noun: "redness" },
  jelo: { mod: "yellow", noun: "yellowness" },
  laso: { mod: "blue-green", noun: "blue-green" },
  walo: { mod: "white / pale", noun: "whiteness" },
  pimeja: { mod: "dark / black", noun: "darkness" },
  kule: { noun: "color", mod: "colorful", verb: "color" },
  // U4 · animals
  soweli: { noun: "land-animal / furry creature", mod: "animal-like" },
  waso: { noun: "bird / flying creature", mod: "bird-like / winged" },
  kala: { noun: "fish / water creature", mod: "fish-like / swimming" },
  akesi: { noun: "reptile / crawling creature", mod: "reptilian / crawling" },
  pipi: { noun: "bug / insect", mod: "bug-like" },
  kijetesantakalu: { noun: "raccoon-kin / weasel-ish creature", mod: "raccoon-ish" },
  // U5 · plants & food
  kasi: { noun: "plant / growing thing", mod: "leafy / plant-y" },
  kili: { noun: "fruit / vegetable", mod: "fruity" },
  pan: { noun: "bread / grain", mod: "grainy / starchy" },
  moku: { verb: "eat / drink", noun: "food", mod: "edible" },
  telo: { noun: "water / liquid", mod: "water / liquid", verb: "wash / water" },
  suwi: { mod: "sweet / cute", noun: "sweetness", vadv: "sweetly" },
  // U6 · body & senses
  luka: { noun: "hand / arm", mod: "hand-related / five", verb: "touch / hold" },
  noka: { noun: "foot / leg", mod: "foot-related / lower" },
  uta: { noun: "mouth", mod: "mouth-related" },
  lukin: { verb: "see / look at", noun: "eye / sight", mod: "seeing / visual" },
  kute: { verb: "hear / listen to", noun: "ear / hearing", mod: "hearing" },
  pilin: { verb: "feel / sense", noun: "feeling / heart", mod: "feeling" },
  sijelo: { noun: "body / physical form", mod: "bodily / physical" },
  // U7 · actions & preverbs
  pali: { verb: "make / do", noun: "work / effort", mod: "working / made" },
  kama: { verb: "come / arrive", noun: "arrival / coming event", mod: "arriving / future", vt: "bring about" },
  tawa: { verb: "go / move", noun: "movement", mod: "moving", vt: "move" },
  awen: { verb: "stay / wait", noun: "staying", mod: "kept / lasting" },
  jo: { verb: "have / hold", noun: "having / possession" },
  pana: { verb: "give / send", noun: "giving" },
  alasa: { verb: "hunt / seek", noun: "hunt / search", mod: "hunting" },
  ken: { verb: "be able / be possible", noun: "ability / possibility", mod: "possible" },
  wile: { verb: "want / need", noun: "want / need", mod: "wanted / needed" },
  // U8 · mind, heart & sound
  sona: { verb: "know / understand", noun: "knowledge", mod: "knowing / wise" },
  olin: { verb: "love / cherish", noun: "love", mod: "beloved" },
  musi: { mod: "fun / playful", noun: "play / art", verb: "play / have fun", vadv: "playfully" },
  lape: { verb: "sleep / rest", noun: "sleep / rest", mod: "sleeping / restful" },
  nasa: { mod: "strange / silly", noun: "strangeness", vadv: "strangely" },
  wawa: { mod: "strong / energetic", noun: "strength / energy", vadv: "strongly" },
  mu: { noun: "animal sound / moo", verb: "make animal sounds", mod: "mooing" },
  kalama: { noun: "sound / noise", verb: "make noise", mod: "noisy", vt: "play / sound out" },
  // U9 · world & sky
  ma: { noun: "land / place", mod: "earthy / outdoor" },
  suno: { noun: "sun / light", mod: "sunny / bright", verb: "shine" },
  mun: { noun: "moon / night-sky object", mod: "lunar / night-sky" },
  sewi: { noun: "high place / the divine", mod: "high / sacred" },
  anpa: { noun: "low place / bottom", mod: "low / humble", verb: "bow / fall", vt: "defeat" },
  seli: { noun: "heat / fire", mod: "hot / warm", verb: "heat / cook" },
  lete: { mod: "cold / raw", noun: "cold", verb: "chill / freeze" },
  kon: { noun: "air / spirit", mod: "airy / unseen" },
  sike: { noun: "circle / round thing", mod: "round / repeating", verb: "circle" },
  // U10 · home & things
  tomo: { noun: "structure / indoor space", mod: "indoor / structural" },
  ilo: { noun: "tool / machine", mod: "mechanical / useful" },
  poki: { noun: "container / box", mod: "containing", verb: "box up / contain" },
  lipu: { noun: "flat thing / document", mod: "flat / paper-like" },
  len: { noun: "cloth / covering", mod: "covered / clothed", verb: "cover / hide" },
  supa: { noun: "surface / table", mod: "flat-topped" },
  kiwen: { mod: "hard / solid", noun: "hard thing / stone" },
  ko: { noun: "goo / paste", mod: "gooey / soft" },
  // U11 · shapes & parts
  palisa: { noun: "stick / rod", mod: "stick-shaped / long" },
  linja: { noun: "line / string", mod: "stringy / trailing" },
  leko: { noun: "block / square", mod: "blocky / square" },
  nena: { noun: "bump / hill", mod: "bumpy" },
  lupa: { noun: "hole / opening", mod: "holey / open" },
  selo: { noun: "outer layer / skin", mod: "outer" },
  sinpin: { noun: "front / wall", mod: "front-facing / vertical" },
  monsi: { noun: "back / rear", mod: "rear" },
  insa: { noun: "inside / middle", mod: "inner" },
  poka: { noun: "side / nearby space", mod: "nearby / beside" },
  // U12 · people & society
  mama: { noun: "parent / caretaker", mod: "parental", verb: "care for / raise" },
  meli: { noun: "woman", mod: "feminine" },
  mije: { noun: "man", mod: "masculine" },
  tonsi: { noun: "nonbinary person", mod: "nonbinary" },
  kulupu: { noun: "group / community", mod: "shared / communal", verb: "gather" },
  esun: { verb: "trade / buy", noun: "market / trade", mod: "commercial" },
  mani: { noun: "money / wealth", mod: "valuable" },
  utala: { verb: "fight / compete", noun: "fight / contest", mod: "fighting" },
  lawa: { noun: "head / mind", verb: "lead / control", mod: "main / ruling" },
  pakala: { mod: "broken / messed-up", verb: "break / wreck", noun: "damage / mistake" },
  moli: { mod: "dead / dying", verb: "die", noun: "death", vt: "kill" },
  jaki: { mod: "gross / dirty", noun: "filth" },
  // U13 · number, time & little words
  wan: { mod: "one / united", noun: "one / unit", verb: "unite" },
  tu: { mod: "two / split", noun: "two / pair", verb: "split" },
  nanpa: { noun: "number", mod: "numbered" },
  tenpo: { noun: "time / moment", mod: "time-related" },
  ale: { mod: "all / every", noun: "everything / all of it" },
  taso: { mod: "only / just", noun: "the only one", vadv: "only" },
  kin: { mod: "also / indeed", adv: "really", vadv: "also" },
  namako: { noun: "extra bit / spice", mod: "extra / bonus" },
  seme: { noun: "what? / which?", mod: "which? / what kind of?" },
  // U14 · ways & connectors
  nasin: { noun: "way / path", mod: "customary / methodical" },
  tan: { noun: "cause / origin", mod: "originating" },
  ante: { mod: "different / other", noun: "difference / other one", verb: "change" },
  weka: { mod: "away / absent", noun: "absence", verb: "remove / get rid of" },
  open: { verb: "open / begin", noun: "start / opening", mod: "open / starting" },
  pini: { verb: "finish / end", noun: "end / finish", mod: "finished / past" },
  sitelen: { noun: "image / writing", verb: "draw / write", mod: "drawn / written" },
  kepeken: { verb: "use", noun: "use / usage" },
  lon: { verb: "exist / be real", mod: "real / true", noun: "existence / truth" },
  unpa: { noun: "sex / intimacy", mod: "sexual", verb: "have sex with" },
};

// The closed preverb set. `t` is the wrapping template ("___" = the inner
// predicate), `neg` the template when ala follows the preverb — so the
// scope difference between "ken ala tawa" and "ken tawa ala" stays visible.
export const PREVERB_GLOSSES = {
  wile: { t: "want to ___", neg: "do not want to ___" },
  kama: { t: "come to ___", neg: "do not come to ___" },
  ken: { t: "can ___", neg: "cannot ___" },
  awen: { t: "continue to ___", neg: "do not continue to ___" },
  lukin: { t: "try to ___", neg: "do not try to ___" },
  sona: { t: "know how to ___", neg: "do not know how to ___" },
  alasa: { t: "seek to ___", neg: "do not seek to ___" },
};

// Prepositions: `attach` glosses a trailing prep phrase ("…, to me"),
// `pred` glosses the prep used AS the predicate ("mi lon tomo").
// `pat` is the grammar pattern id that unlocks the reading.
export const PREP_GLOSSES = {
  tawa: { attach: "to", pred: "go to ___", pat: "tawa" },
  lon: { attach: "at", pred: "be at ___", pat: "prep" },
  kepeken: { attach: "using", pred: "use ___", pat: "prep" },
  tan: { attach: "from", pred: "come from ___", pat: "prep" },
  sama: { attach: "like", pred: "be like ___", pat: "prep" },
};

const PRONOUNS = {
  mi: { subj: "I", obj: "me", poss: "my", cop: "am" },
  sina: { subj: "you", obj: "you", poss: "your", cop: "are" },
  ona: { subj: "they", obj: "them", poss: "their", cop: "are" },
  ni: { subj: "this", obj: "this", poss: "this", cop: "is" },
};

/* ---------------- word helpers ---------------- */
const sense = (s) => s.split(" / ")[0];
const isParticle = (t) => PARTICLES.includes(t);
const roleOf = (w) => ROLE_GLOSSES[w] || { noun: (WORDS[w] && WORDS[w].g) || w };
const primaryRole = (w) => Object.keys(roleOf(w))[0];
const nounSense = (w) => sense(roleOf(w).noun || WORDS[w].g);
const modSense = (w) =>
  PRONOUNS[w] ? PRONOUNS[w].poss : sense(roleOf(w).mod || roleOf(w).noun || WORDS[w].g);
const advSense = (w) => sense(roleOf(w).adv || roleOf(w).mod || WORDS[w].g);
const verbSense = (w) => (roleOf(w).verb ? sense(roleOf(w).verb) : null);

const brk = (s) => (s.includes(" ") ? `[${s}]` : s);

// Leading words that already determine the noun phrase, so no article.
const NO_ART = new Set([
  "my", "your", "their", "this", "that", "what", "which", "many", "much",
  "no", "all", "every", "one", "two", "some", "very", "I", "you", "me",
  "we", "us", "they", "them", "it", "these", "number",
]);
const MASS = new Set(["food", "money", "water", "everything", "nothing"]);
// mi mute = we — plural pronouns are their own words in English
const PRONOUN_MUTE = {
  mi: { subj: "we", obj: "us" },
  sina: { subj: "you all", obj: "you all" },
  ona: { subj: "they", obj: "them" },
  ni: { subj: "these", obj: "these" },
};
// numbers that combine with nanpa into ordinals (nanpa wan = number one)
const NUM_WORDS = { wan: "one", tu: "two", luka: "five" };
// modifiers that pluralize what they count (jan mute = many people)
const PLURALIZERS = new Set(["mute", "tu", "ale"]);
const PLURAL_IRR = { person: "people", child: "children", foot: "feet", tooth: "teeth", fish: "fish" };
const pluralize = (g) => {
  const parts = g.split(" ");
  const m = parts.pop().match(/^(.*?)(\]*)$/);
  const w = m[1];
  let pl;
  if (PLURAL_IRR[w]) pl = PLURAL_IRR[w];
  else if (MASS.has(w) || /ness$/.test(w)) pl = w;
  else if (/[^aeiou]y$/.test(w)) pl = `${w.slice(0, -1)}ies`;
  else if (/(s|sh|ch|x|z)$/.test(w)) pl = `${w}es`;
  else pl = `${w}s`;
  return [...parts, pl + m[2]].join(" ");
};
const leadWord = (g) => g.split(" ")[0].replace(/[[\]?!.,]/g, "");
const aOrAn = (g) => (/^[aeiou]/i.test(leadWord(g)) ? "an" : "a");
// a bare ala right after a preposition is negation, not a "to nothing" object
const bareAla = (np) => np.head === "ala" && !np.items.length;
const withThe = (g) => (NO_ART.has(leadWord(g)) ? g : `the ${g}`);
const withA = (g) => {
  const lead = leadWord(g);
  if (NO_ART.has(lead) || MASS.has(lead)) return g;
  return `${aOrAn(g)} ${g}`;
};

const IRREGULAR = { have: "has", do: "does", be: "is" };
const NO_CONJ = new Set(["can", "cannot", "must", "may"]);
const conj = (phrase) => {
  const [w, ...rest] = phrase.split(" ");
  if (NO_CONJ.has(w)) return phrase;
  const c = IRREGULAR[w] || (/(s|sh|ch|x|z|o)$/.test(w) ? `${w}es` : `${w}s`);
  return [c, ...rest].join(" ");
};

/* ---------------- errors ---------------- */
const ERR = {
  empty: () => ({ code: "empty", message: "Add some tiles to build a sentence." }),
  unknown: (w) => ({ code: "unknown-word", message: `"${w}" isn't a toki pona word.` }),
  piStart: () => ({
    code: "pi-start",
    message: "pi can't start a phrase — it regroups describing words after a head word.",
  }),
  piOneWord: (grp) => ({
    code: "pi-one-word",
    message: "pi needs two or more words after it — it turns them into one modifier.",
    hint: grp.length === 1
      ? `With a single describing word, drop the pi: just say the head word plus "${grp[0]}".`
      : "Give pi at least two words to group, or remove it.",
  }),
  piParticle: (t) => ({
    code: "pi-particle",
    message: `"${t}" can't go inside a pi group — pi only groups content words.`,
  }),
  miSinaLi: (w) => ({
    code: "mi-sina-li",
    message: `A bare ${w} goes straight to its predicate — no li.`,
    hint: `Say "${w} moku", not "${w} li moku". (A modified subject like "${w} mute" does take li.)`,
  }),
  liFirst: () => ({
    code: "li-first",
    message: "li links a subject to its predicate — put the subject before it.",
  }),
  eNoHead: () => ({
    code: "e-no-head",
    message: "e marks what an action lands on — it needs a verb before it.",
  }),
  liMissing: () => ({
    code: "li-missing",
    message: "This looks like an action on an object — a subject other than mi or sina needs li before its verb.",
  }),
  dangling: (p) => ({
    code: "dangling-particle",
    message: `${p} is missing the words around it.`,
  }),
};

/* ---------------- token utilities ---------------- */
const splitOn = (tokens, particle) => {
  const parts = [[]];
  tokens.forEach((t) => (t === particle ? parts.push([]) : parts[parts.length - 1].push(t)));
  return parts;
};

/* ---------------- NP parsing ---------------- */
// head simpleMods* (pi group)* — a pi group runs until the next pi or the
// end of the segment, and needs at least two words. Each pi starts a new
// SIBLING group on the same head (pi never nests).
function parseNP(tokens) {
  if (!tokens.length) return { error: ERR.empty() };
  const [head, ...rest] = tokens;
  if (head === "pi") return { error: ERR.piStart() };
  if (isParticle(head)) return { error: ERR.dangling(head) };
  if (!WORDS[head]) return { error: ERR.unknown(head) };
  const items = [];
  let i = 0;
  while (i < rest.length) {
    const t = rest[i];
    if (t === "pi") {
      let j = i + 1;
      const grp = [];
      while (j < rest.length && rest[j] !== "pi") grp.push(rest[j++]);
      if (grp.length < 2) return { error: ERR.piOneWord(grp) };
      const bad = grp.find(isParticle);
      if (bad) return { error: ERR.piParticle(bad) };
      const sub = parseNP(grp);
      if (sub.error) return sub;
      items.push({ kind: "pi", np: sub.np });
      i = j;
    } else if (t === "ala") {
      items.push({ kind: "neg" });
      i += 1;
    } else if (isParticle(t)) {
      return { error: ERR.dangling(t) };
    } else if (!WORDS[t]) {
      return { error: ERR.unknown(t) };
    } else {
      items.push({ kind: "mod", word: t });
      i += 1;
    }
  }
  return { np: { head, items } };
}

// Broad compositional gloss of a noun phrase. Modifiers PREFIX and wrap
// what came before ("tomo telo nasa" → strange [water structure]); a pi
// group SUFFIXES as "of [...]" ("tomo pi telo nasa" → structure of
// [strange water]) — the bracket move that makes pi visible.
function npGloss(np, form = "subj") {
  const pform = form === "obj" ? "obj" : "subj";
  if (
    PRONOUN_MUTE[np.head] &&
    np.items.length === 1 &&
    np.items[0].kind === "mod" &&
    np.items[0].word === "mute"
  ) {
    return PRONOUN_MUTE[np.head][pform];
  }
  let s = PRONOUNS[np.head] ? PRONOUNS[np.head][pform] : nounSense(np.head);
  let start = 0;
  if (np.head === "nanpa" && np.items[0] && np.items[0].kind === "mod" && NUM_WORDS[np.items[0].word]) {
    s = `number ${NUM_WORDS[np.items[0].word]}`;
    start = 1;
  }
  for (let i = start; i < np.items.length; i++) {
    const it = np.items[i];
    const nxt = np.items[i + 1];
    if (it.kind === "mod" && it.word === "nanpa" && nxt && nxt.kind === "mod" && NUM_WORDS[nxt.word]) {
      s = `${brk(s)} number ${NUM_WORDS[nxt.word]}`;
      i += 1;
    } else if (it.kind === "mod") {
      const inner = PLURALIZERS.has(it.word) ? pluralize(s) : s;
      s = `${modSense(it.word)} ${brk(inner)}`;
    } else if (it.kind === "neg") s = `no ${brk(s)}`;
    else s = `${brk(s)} of [${npGloss(it.np, "obj")}]`;
  }
  return s;
}

// Fold NP items as an adjective/adverb phrase ("suli mute" → very big,
// "pona ala" → not good) — used for copular predicates on mod-first heads.
function modFold(head, items) {
  let s = modSense(head);
  items.forEach((it) => {
    if (it.kind === "mod") s = `${advSense(it.word)} ${brk(s)}`;
    else if (it.kind === "neg") s = `not ${brk(s)}`;
    else s = `${brk(s)} of [${npGloss(it.np, "obj")}]`;
  });
  return s;
}

/* ---------------- predicate parsing ---------------- */
// Split a predicate-side segment at its first preposition-with-object,
// returning both readings (prep phrase vs plain modifiers) when the
// pattern is unlocked. Further preps chain greedily.
function prepSplits(tokens, has) {
  const alts = [{ npTokens: tokens, preps: [] }];
  for (let i = 1; i < tokens.length; i++) {
    const g = PREP_GLOSSES[tokens[i]];
    if (g && has(g.pat) && i < tokens.length - 1) {
      const preps = [];
      let cur = null;
      let ok = true;
      for (let j = i; j < tokens.length; j++) {
        const t = tokens[j];
        const pg = PREP_GLOSSES[t];
        if (pg && has(pg.pat) && (cur === null || cur.npTokens.length)) {
          cur = { word: t, npTokens: [] };
          preps.push(cur);
        } else if (cur) {
          cur.npTokens.push(t);
        } else {
          ok = false;
          break;
        }
      }
      if (ok && preps.every((p) => p.npTokens.length)) {
        alts.push({ npTokens: tokens.slice(0, i), preps });
      }
      break;
    }
  }
  return alts;
}

// All readings of one predicate segment (between li's). Handles the
// preverb-vs-head branch, e-objects, trailing preps, X-ala-X questions
// and anu alternatives. Returns { alts, errors }.
function predicateReadings(seg, has) {
  const errors = [];
  const alts = [];

  // anu — parse each alternative separately, pair them back up later
  const anuParts = splitOn(seg, "anu");
  if (anuParts.length > 1) {
    if (anuParts.some((p) => !p.length)) return { alts: [], errors: [ERR.dangling("anu")] };
    const parsed = anuParts.map((p) => predicateReadings(p, has));
    parsed.forEach((r) => errors.push(...r.errors));
    if (parsed.every((r) => r.alts.length)) {
      const best = parsed.map((r) => r.alts.sort((a, b) => b.score - a.score)[0]);
      alts.push({ kind: "anu", options: best, score: best[0].score });
    }
    return { alts, errors };
  }

  // X ala X → yes/no question on X
  let tokens = seg;
  let ynq = false;
  for (let i = 0; i + 2 < tokens.length; i++) {
    if (tokens[i + 1] === "ala" && tokens[i] === tokens[i + 2]) {
      tokens = [...tokens.slice(0, i + 1), ...tokens.slice(i + 3)];
      ynq = true;
      break;
    }
  }

  const eParts = splitOn(tokens, "e");
  const core = eParts[0];
  const objSegs = eParts.slice(1);
  if (!core.length) return { alts: [], errors: [ERR.eNoHead()] };
  if (objSegs.some((s) => !s.length)) return { alts: [], errors: [ERR.dangling("e")] };

  // objects — the LAST object segment may carry the predicate's trailing
  // prep phrases ("pana e kili tawa sina"); earlier ones are plain NPs.
  const objAlts = [{ objects: [], preps: [], error: null }];
  objSegs.forEach((oSeg, k) => {
    const last = k === objSegs.length - 1;
    const splits = last ? prepSplits(oSeg, has) : [{ npTokens: oSeg, preps: [] }];
    const parsedSplits = splits
      .map((sp) => ({ sp, np: parseNP(sp.npTokens) }))
      .filter(({ np }) => {
        if (np.error) errors.push(np.error);
        return !np.error;
      });
    const next = [];
    objAlts.forEach((oa) => {
      parsedSplits.forEach(({ sp, np }) => {
        const preps = [];
        let bad = false;
        sp.preps.forEach((p) => {
          const pnp = parseNP(p.npTokens);
          if (pnp.error) {
            errors.push(pnp.error);
            bad = true;
          } else if (bareAla(pnp.np)) bad = true;
          else preps.push({ word: p.word, np: pnp.np });
        });
        if (!bad) next.push({ objects: [...oa.objects, np.np], preps });
      });
    });
    objAlts.length = 0;
    objAlts.push(...next);
  });
  if (objSegs.length && !objAlts.length) return { alts, errors };

  // core — recursive preverb branching, then head-phrase readings
  const eatCore = (rest, preverbs) => {
    if (!rest.length) return;
    const t0 = rest[0];
    if (PREVERB_GLOSSES[t0] && has("preverb")) {
      const neg = rest[1] === "ala";
      const after = rest.slice(neg ? 2 : 1);
      if (after.length) eatCore(after, [...preverbs, { word: t0, neg }]);
    }
    headReadings(rest, preverbs);
  };

  const headReadings = (rest, preverbs) => {
    const pvScore = preverbs.length * 2;
    // preposition as the whole predicate: "mi lon tomo", "mi tawa sina"
    const pg = PREP_GLOSSES[rest[0]];
    if (pg && has(pg.pat) && rest.length > 1 && !objSegs.length) {
      const comp = parseNP(rest.slice(1));
      if (comp.error) {
        errors.push(comp.error);
      } else if (!bareAla(comp.np)) {
        pushAlt({ kind: "prepHead", head: rest[0], np: comp.np, preverbs, score: 1.6 + pvScore });
      }
    }
    prepSplits(rest, has).forEach((sp, spIdx) => {
      if (!sp.npTokens.length) return;
      const parsed = parseNP(sp.npTokens);
      if (parsed.error) {
        errors.push(parsed.error);
        return;
      }
      const preps = [];
      let bad = false;
      sp.preps.forEach((p) => {
        const pnp = parseNP(p.npTokens);
        if (pnp.error) {
          errors.push(pnp.error);
          bad = true;
        } else if (bareAla(pnp.np)) bad = true;
        else preps.push({ word: p.word, np: pnp.np });
      });
      if (bad) return;
      const np = parsed.np;
      const prepScore = preps.length * 0.8 + (spIdx > 0 ? 0.2 : 0);
      const pref = primaryRole(np.head);
      if (verbSense(np.head)) {
        pushAlt({
          kind: "verbal", head: np.head, np, preverbs, preps,
          score: (pref === "verb" ? 1.5 : 0.7) + pvScore + prepScore,
        });
      }
      // "kama X" as a full verb: become X
      if (np.head === "kama" && np.items.length && np.items[0].kind === "mod" && !objSegs.length) {
        pushAlt({
          kind: "become", head: np.head, np, preverbs, preps,
          score: 1.2 + pvScore + prepScore,
        });
      }
      if (!objSegs.length) {
        pushAlt({
          kind: "copular", head: np.head, np, preverbs, preps,
          score: (pref !== "verb" ? 1.5 : 0.7) + pvScore + prepScore,
        });
      }
    });
  };

  const pushAlt = (a) => {
    objAlts.length
      ? objAlts.forEach((oa) =>
          alts.push({
            ...a,
            objects: oa.objects,
            preps: [...(a.preps || []), ...oa.preps],
            score: a.score + oa.preps.length * 0.8,
            ynq,
          })
        )
      : alts.push({ ...a, objects: [], preps: a.preps || [], ynq });
  };

  eatCore(core, []);
  return { alts, errors };
}

/* ---------------- glossing predicates ---------------- */
function copulaFor(subjects) {
  if (!subjects) return "is";
  if (subjects.length > 1) return "are";
  const np = subjects[0];
  if (PRONOUNS[np.head] && !np.items.length) return PRONOUNS[np.head].cop;
  const lead = leadWord(npGloss(np));
  if (["many", "all", "two", "some", "we", "you", "they", "these"].includes(lead)) return "are";
  return "is";
}

function copularGloss(np) {
  const pref = primaryRole(np.head);
  if (pref === "mod") return modFold(np.head, np.items);
  return withA(npGloss(np, "obj"));
}

// A verb's trailing modifiers as a plain adverb phrase ("moku mute" →
// eat a lot) when the first one has an authored verb-adverb sense;
// returns null to fall back to the "in a ___ way" template.
const mannerAdv = (items) => {
  if (items[0].kind !== "mod" || !roleOf(items[0].word).vadv) return null;
  let s = sense(roleOf(items[0].word).vadv);
  for (const it of items.slice(1)) {
    if (it.kind === "mod") s = `${advSense(it.word)} ${s}`;
    else if (it.kind === "neg") s = `not ${s}`;
    else return null;
  }
  return s;
};

// Render one predicate alternative against its subject(s). `subjects` is
// null for imperatives/fragments (bare, unconjugated forms).
function glossPred(alt, subjects) {
  if (alt.kind === "anu") {
    const parts = alt.options.map((o) => glossPred(o, subjects));
    return `${parts.join(" — or ")}?`;
  }
  const cop = copulaFor(subjects);
  const third = !!subjects && cop === "is" && !alt.preverbs.length;
  let core;
  if (alt.kind === "prepHead") {
    const tpl = PREP_GLOSSES[alt.head].pred;
    core = tpl.replace("___", withThe(npGloss(alt.np, "obj")));
    if (core.startsWith("be ") && !alt.preverbs.length && subjects) {
      core = `${cop} ${core.slice(3)}`;
    } else if (third) {
      core = conj(core);
    }
  } else if (alt.kind === "become") {
    const rest = { head: alt.np.items[0].word, items: alt.np.items.slice(1) };
    const comp = primaryRole(rest.head) === "mod" && !rest.items.length
      ? modSense(rest.head)
      : withA(npGloss(rest, "obj"));
    core = `become ${comp}`;
    if (third) core = conj(core);
  } else if (alt.kind === "verbal") {
    const negFirst = alt.np.items[0] && alt.np.items[0].kind === "neg";
    const manner = alt.np.items.slice(negFirst ? 1 : 0);
    const vt = alt.objects.length && roleOf(alt.head).vt;
    core = vt ? sense(vt) : verbSense(alt.head);
    if (negFirst) core = alt.preverbs.length ? `not ${core}` : `do not ${core}`;
    if (alt.objects.length) {
      core += ` ${alt.objects.map((o) => withThe(npGloss(o, "obj"))).join(" and ")}`;
    }
    if (manner.length) {
      const adv = mannerAdv(manner);
      if (adv) core += ` ${adv}`;
      else {
        const m = modFold(manner[0].kind === "mod" ? manner[0].word : alt.head, manner.slice(1));
        if (manner[0].kind === "mod") core += ` in ${aOrAn(m)} ${m} way`;
        else if (manner[0].kind === "pi") core += ` in a way of [${npGloss(manner[0].np, "obj")}]`;
      }
    }
    if (core.startsWith("be ") && subjects && !alt.preverbs.length) {
      core = `${cop} ${core.slice(3)}`;
    } else if (third) core = conj(core);
  } else {
    // copular
    const g = copularGloss(alt.np);
    core = alt.preverbs.length || !subjects ? `be ${g}` : `${cop} ${g}`;
  }
  [...alt.preverbs].reverse().forEach((pv) => {
    const tpl = pv.neg ? PREVERB_GLOSSES[pv.word].neg : PREVERB_GLOSSES[pv.word].t;
    core = tpl.replace("___", core.startsWith("not ") ? `[${core}]` : core);
  });
  alt.preps.forEach((p) => {
    core += `, ${PREP_GLOSSES[p.word].attach} ${withThe(npGloss(p.np, "obj"))}`;
  });
  if (alt.ynq) core += " — yes or no?";
  return core;
}

const predNotes = (alt) => {
  const notes = [];
  if (alt.kind === "copular" && ["pona", "ike"].includes(alt.head) &&
      alt.preps.some((p) => p.word === "tawa")) {
    notes.push("toki pona's way of (dis)liking: “X is good to me” means “I like X.”");
  }
  return notes;
};

/* ---------------- sentence-level parse ---------------- */
const cartesian = (lists, cap) => {
  let combos = [[]];
  lists.forEach((list) => {
    const next = [];
    combos.forEach((c) => list.forEach((x) => next.push([...c, x])));
    combos = next.slice(0, cap);
  });
  return combos;
};

// Readings of one clause (no la). Returns { readings, errors }.
function clauseReadings(tokens, has, opts = {}) {
  const errors = [];
  const readings = [];
  const { allowFragment = true } = opts;

  // imperative / vocative: [NP] o predicate
  const oIdx = tokens.indexOf("o");
  if (oIdx !== -1) {
    const before = tokens.slice(0, oIdx);
    const after = tokens.slice(oIdx + 1);
    if (!after.length) return { readings, errors: [ERR.dangling("o")] };
    let voc = null;
    if (before.length) {
      const v = parseNP(before);
      if (v.error) return { readings, errors: [v.error] };
      voc = v.np;
    }
    const pr = predicateReadings(after, has);
    errors.push(...pr.errors);
    pr.alts.forEach((alt) => {
      const g = glossPred(alt, null);
      readings.push({
        gloss: `${voc ? `${withThe(npGloss(voc))} — ` : ""}${g}!`,
        score: 9 + alt.score,
        tags: ["imperative", ...tagsOf([alt])],
        tree: { type: "imperative", vocative: voc, predicates: [alt] },
        notes: ["o makes it a request or a call-out.", ...predNotes(alt)],
      });
    });
    return { readings, errors };
  }

  const liParts = splitOn(tokens, "li");
  let subjectSeg;
  let predSegs;
  if (liParts.length === 1) {
    const [t0, ...rest] = tokens;
    if ((t0 === "mi" || t0 === "sina") && rest.length) {
      subjectSeg = [t0];
      predSegs = [rest];
    } else if (t0 === "e") {
      return { readings, errors: [ERR.eNoHead()] };
    } else if (tokens.includes("e")) {
      return { readings, errors: [ERR.liMissing()] };
    } else {
      subjectSeg = null;
      predSegs = null;
    }
  } else {
    if (!liParts[0].length) return { readings, errors: [ERR.liFirst()] };
    if (liParts.some((p) => !p.length)) return { readings, errors: [ERR.dangling("li")] };
    if (liParts[0].length === 1 && ["mi", "sina"].includes(liParts[0][0])) {
      return { readings, errors: [ERR.miSinaLi(liParts[0][0])] };
    }
    subjectSeg = liParts[0];
    predSegs = liParts.slice(1);
  }

  if (predSegs) {
    const subjParts = splitOn(subjectSeg, "en");
    if (subjParts.some((p) => !p.length)) return { readings, errors: [ERR.dangling("en")] };
    const subjects = [];
    for (const p of subjParts) {
      const s = parseNP(p);
      if (s.error) return { readings, errors: [s.error] };
      subjects.push(s.np);
    }
    const subjStr = subjects
      .map((np) => (PRONOUNS[np.head] && !np.items.length ? PRONOUNS[np.head].subj : withThe(npGloss(np))))
      .join(" and ");
    const predAlternatives = predSegs.map((seg) => {
      const r = predicateReadings(seg, has);
      errors.push(...r.errors);
      return r.alts.sort((a, b) => b.score - a.score).slice(0, 4);
    });
    if (predAlternatives.every((l) => l.length)) {
      cartesian(predAlternatives, 8).forEach((combo) => {
        const glosses = combo.map((alt) => glossPred(alt, subjects));
        readings.push({
          gloss: `${subjStr} ${glosses.join(" and ")}`,
          score: 10 + combo.reduce((n, a) => n + a.score, 0),
          tags: tagsOf(combo),
          tree: { type: "clause", subjects, predicates: combo },
          notes: combo.flatMap(predNotes),
        });
      });
    }
  }

  // bare noun-phrase fragment ("soweli suli")
  if (allowFragment && !tokens.some((t) => ["e", "li"].includes(t))) {
    const f = parseNP(tokens);
    if (f.error) errors.push(f.error);
    else {
      const pref = primaryRole(f.np.head);
      const g = pref === "mod" && !f.np.items.length ? modSense(f.np.head) : withA(npGloss(f.np));
      readings.push({
        gloss: g,
        score: readings.length ? 1 : 3,
        tags: ["fragment"],
        tree: { type: "fragment", np: f.np },
      });
    }
  }

  return { readings, errors };
}

const tagsOf = (preds) => {
  const tags = new Set();
  preds.forEach((a) => {
    if (a.kind === "anu") return tags.add("anu");
    if (a.preverbs.length) tags.add("preverb");
    tags.add(a.kind);
    if (a.preps.length) tags.add("prep");
    if (hasPi(a.np)) tags.add("pi");
    (a.objects || []).forEach((o) => hasPi(o) && tags.add("pi"));
  });
  return [...tags];
};
const hasPi = (np) =>
  !!np && np.items.some((it) => it.kind === "pi" || (it.kind === "pi" && hasPi(it.np)));

/* ---------------- phrase matching ("try saying" prompt) ----------------
   Compares a learner-built sentence against a target teaching phrase for
   the compose sandbox's "try saying" chip. Beyond exact string equality,
   toki pona has some genuinely free word order that a naive string
   compare would mark wrong:
     - e-chain object order is free ("moku e pan e kili" = "moku e kili e pan")
     - en-chain subject order is free ("mi en sina" = "sina en mi")
     - li-chain predicate order is free ("li toki li moku" = "li moku li toki")
     - trailing prep-phrase order is free
     - a bare-NP copular clause can flip subject and predicate
       ("nimi mi li ni" = "ni li nimi mi")
   NP-internal modifier order and preverb order are NOT free (they change
   or clarify meaning), so those stay strictly ordered in the canonical key.
   la-context sentences are handled conservatively: contexts aren't part of
   the parse tree, so equivalence isn't attempted for them at all. */

// NP -> ordered token key. Modifier/pi order is meaningful, so items are
// walked in sequence, never sorted.
function npKey(np) {
  if (!np) return "";
  let s = np.head;
  np.items.forEach((it) => {
    if (it.kind === "mod") s += ` ${it.word}`;
    else if (it.kind === "neg") s += " ala";
    else if (it.kind === "pi") s += ` pi(${npKey(it.np)})`;
  });
  return s;
}

// Predicate kind bucket for keying — verbal/prepHead/become are their own
// kind, copular gets special handling by the caller (subject/pred swap).
function predKey(pred) {
  if (pred.kind === "anu") {
    return `anu[${pred.options.map(predKey).sort().join("|")}]`;
  }
  const preverbs = pred.preverbs.map((p) => `${p.word}${p.neg ? "!" : ""}`).join(",");
  const objects = (pred.objects || []).map(npKey).sort().join(",");
  const preps = (pred.preps || [])
    .map((p) => `${p.word}:${npKey(p.np)}`)
    .sort()
    .join(",");
  return `${pred.kind}|${preverbs}|${npKey(pred.np)}|${objects}|${preps}|${pred.ynq ? "?" : ""}`;
}

// Skeleton for "close" comparisons: just the preverb chain + head, ignoring
// modifiers/objects/preps so near-miss sentences ("...moku e pan" vs
// "...moku e kili") still register as structurally close.
function predSkeleton(pred) {
  if (pred.kind === "anu") {
    return `anu[${pred.options.map(predSkeleton).sort().join("|")}]`;
  }
  const preverbs = pred.preverbs.map((p) => `${p.word}${p.neg ? "!" : ""}`).join(",");
  return `${preverbs}|${pred.head}`;
}

// Canonical key(s) for one reading's tree. A clause normally returns one
// key, but a bare-NP copular clause (one subject, one plain copular
// predicate) also returns the subject/predicate-swapped key, so "X li Y"
// and "Y li X" collide when that swap is legitimate toki pona.
function treeKeys(tree) {
  if (!tree) return [];
  if (tree.type === "fragment") return [`frag|${npKey(tree.np)}`];
  if (tree.type === "imperative") {
    const voc = tree.vocative ? npKey(tree.vocative) : "";
    const preds = tree.predicates.map(predKey).sort().join(",");
    return [`imp|${voc}|${preds}`];
  }
  if (tree.type === "clause") {
    const subjKeys = tree.subjects.map(npKey).sort();
    const predKeys = tree.predicates.map(predKey).sort();
    const keys = [`clause|${subjKeys.join(",")}|${predKeys.join(",")}`];
    if (
      tree.subjects.length === 1 &&
      tree.predicates.length === 1 &&
      tree.predicates[0].kind === "copular" &&
      !tree.predicates[0].preverbs.length &&
      !(tree.predicates[0].objects || []).length &&
      !(tree.predicates[0].preps || []).length &&
      !tree.predicates[0].ynq
    ) {
      const pair = [npKey(tree.subjects[0]), npKey(tree.predicates[0].np)].sort();
      keys.push(`swap|${pair.join("~")}`);
    }
    return keys;
  }
  return [];
}

// "close" comparison: same clause type, same sorted subjects, same sorted
// predicate skeletons (preverbs + head only).
function closeKey(tree) {
  if (!tree) return null;
  if (tree.type === "fragment") return `frag|${tree.np.head}`;
  if (tree.type === "clause") {
    const subjKeys = tree.subjects.map(npKey).sort();
    const skels = tree.predicates.map(predSkeleton).sort();
    return `clause|${subjKeys.join(",")}|${skels.join(",")}`;
  }
  if (tree.type === "imperative") {
    const voc = tree.vocative ? npKey(tree.vocative) : "";
    const skels = tree.predicates.map(predSkeleton).sort();
    return `imp|${voc}|${skels.join(",")}`;
  }
  return null;
}

const normSpace = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");
const hasLa = (s) => normSpace(s).split(" ").includes("la");

// Top non-fragment readings (up to 4), falling back to fragment readings
// only if that's all there is — fragment targets like "soweli suli" exist.
function topReadings(text) {
  const { readings } = parseSentence(text);
  if (!readings.length) return [];
  const nonFrag = readings.filter((r) => r.tree && r.tree.type !== "fragment");
  return (nonFrag.length ? nonFrag : readings).slice(0, 4);
}

function isEquivalent(sentence, targetTp) {
  const a = topReadings(sentence);
  const b = topReadings(targetTp);
  if (!a.length || !b.length) return false;
  const aKeys = new Set(a.flatMap((r) => treeKeys(r.tree)));
  const bKeys = new Set(b.flatMap((r) => treeKeys(r.tree)));
  for (const k of aKeys) if (bKeys.has(k)) return true;
  return false;
}

function isClose(sentence, targetTp) {
  const a = topReadings(sentence)[0];
  const b = topReadings(targetTp)[0];
  if (!a || !b) return false;
  const ak = closeKey(a.tree);
  const bk = closeKey(b.tree);
  return !!ak && ak === bk;
}

// Match a learner-built sentence against a target teaching phrase.
// Returns "exact" | "equivalent" | "close" | null. Never throws.
export function matchPhrase(sentence, target) {
  try {
    if (!target) return null;
    const built = normSpace(sentence);
    if (!built) return null;
    const tpCandidates = [target.tp, ...(target.alt || [])].filter(Boolean);

    // exact
    if (tpCandidates.some((tp) => normSpace(tp) === built)) return "exact";

    // la sentences: skip equivalence/close checks (contexts aren't in the
    // tree, so we can't safely reason about them) — exact-or-null only.
    if (hasLa(built) || tpCandidates.some((tp) => hasLa(tp))) return null;

    // equivalent
    for (const tp of tpCandidates) {
      if (isEquivalent(built, tp)) return "equivalent";
    }

    // close (against the primary target only)
    if (isClose(built, target.tp)) return "close";

    return null;
  } catch (e) {
    return null;
  }
}

// predictive-typing candidates for the compose input: normalize the typed
// text (lowercase, letters only), then rank an exact match first, prefix
// matches next (alphabetical), and substring matches last (alphabetical) —
// capped at 8, no duplicates.
export function completions(text, available) {
  const norm = String(text || "").toLowerCase().replace(/[^a-z]/g, "");
  if (!norm) return [];
  const pool = [...new Set(available || [])];
  const exact = pool.filter((w) => w === norm);
  const starts = pool.filter((w) => w !== norm && w.startsWith(norm)).sort();
  const contains = pool.filter((w) => w !== norm && !w.startsWith(norm) && w.includes(norm)).sort();
  return [...exact, ...starts, ...contains].slice(0, 8);
}

/* ---------------- public API ---------------- */
// Grammar patterns whose introducing lesson the learner has completed —
// pass the progress `completed` map straight in, feed the result to
// parseSentence's `patterns` option.
export function unlockedPatternIds(completed = {}) {
  return new Set(PATTERNS.filter((p) => completed[p.at]).map((p) => p.id));
}

// Parse a learner-built sentence into ranked broad-gloss readings.
// opts.patterns: Set/array of unlocked pattern ids (omit = everything).
export function parseSentence(input, opts = {}) {
  const patterns = opts.patterns
    ? opts.patterns instanceof Set ? opts.patterns : new Set(opts.patterns)
    : null;
  const has = (id) => !patterns || patterns.has(id);

  let tokens = String(input || "")
    .toLowerCase()
    .replace(/[.,!?;:]+/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  const result = { input, tokens: [...tokens], readings: [], errors: [], question: false, emphasis: false };
  if (!tokens.length) {
    result.errors = [ERR.empty()];
    return result;
  }
  const badWord = tokens.find((t) => !WORDS[t] && !isParticle(t));
  if (badWord) {
    result.errors = [ERR.unknown(badWord)];
    return result;
  }

  // trailing a = emotion, not structure
  while (tokens.length > 1 && tokens[tokens.length - 1] === "a") {
    tokens.pop();
    result.emphasis = true;
  }
  if (tokens.length === 1 && tokens[0] === "a") {
    result.readings = [{ gloss: "ah! / oh! (pure feeling)", score: 1, tags: ["interjection"], tree: { type: "interjection" } }];
    return result;
  }

  // sentence-initial taso/kin are discourse words: "but ..." / "also ..."
  let discourse = "";
  if (tokens.length > 1 && tokens[1] !== "la") {
    if (tokens[0] === "taso") {
      discourse = "but ";
      tokens = tokens.slice(1);
    } else if (tokens[0] === "kin") {
      discourse = "also, ";
      tokens = tokens.slice(1);
    }
  }

  result.question = /\?/.test(String(input)) || tokens.includes("seme");

  const laParts = splitOn(tokens, "la");
  if (laParts.some((p) => !p.length)) {
    result.errors = [ERR.dangling("la")];
    return result;
  }
  let main = laParts[laParts.length - 1];
  const ctxSegs = laParts.slice(0, -1);

  // trailing "anu seme" turns a statement into "..., right?"
  let rightTag = false;
  if (main.length > 2 && main[main.length - 2] === "anu" && main[main.length - 1] === "seme") {
    main = main.slice(0, -2);
    rightTag = true;
    result.question = true;
  }

  // each context: best clause reading, else noun phrase
  const ctxGlosses = [];
  for (const seg of ctxSegs) {
    if (seg.length === 1 && (seg[0] === "kin" || seg[0] === "taso")) {
      ctxGlosses.push(seg[0] === "kin" ? "also" : "even so");
      continue;
    }
    const c = clauseReadings(seg, has);
    const best = c.readings.sort((a, b) => b.score - a.score)[0];
    if (!best) {
      result.errors = c.errors.length ? [c.errors[0]] : [ERR.dangling("la")];
      return result;
    }
    ctxGlosses.push(
      best.tags.includes("fragment") ? `with ${brk(best.gloss.replace(/^an? /, ""))} as context` : `if/when [${best.gloss}]`
    );
  }
  const prefix = ctxGlosses.length ? `${ctxGlosses.join(", ")} — ` : "";

  const { readings, errors } = clauseReadings(main, has);
  const seen = new Map();
  readings.forEach((r) => {
    if (r.tags.includes("anu")) result.question = true;
    if (r.tree.predicates && r.tree.predicates.some((p) => p.ynq)) result.question = true;
    let gloss = `${discourse}${prefix}${r.gloss}`;
    if (rightTag) gloss += " — right?";
    if (result.question && !/[?!]$/.test(gloss)) gloss += "?";
    if (result.emphasis && !/!$/.test(gloss)) gloss += "!";
    gloss = gloss.replace(/\?{2,}$/, "?");
    const kept = seen.get(gloss);
    if (!kept || kept.score < r.score) seen.set(gloss, { ...r, gloss });
  });
  result.readings = [...seen.values()].sort((a, b) => b.score - a.score).slice(0, 6);
  if (result.emphasis) {
    result.readings.forEach((r) => {
      r.notes = [...(r.notes || []), "the trailing a adds pure feeling — delight, emphasis, a sigh."];
    });
  }
  if (!result.readings.length) {
    const uniq = [];
    errors.forEach((e) => {
      if (!uniq.some((u) => u.code === e.code)) uniq.push(e);
    });
    result.errors = uniq.length ? uniq : [ERR.empty()];
  }
  return result;
}
